import {
  Box,
  Button,
  Collapse,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Heading,
  Icon,
  Image,
  Input,
  SlideFade,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { HiChevronRight, HiXMark } from "react-icons/hi2";
import React, { useEffect, useState } from "react";

import { ERROR_MESSAGES } from "../constants/error_messages";
import { Link } from "react-router-dom";
import { images } from "../assets/images/images";
import { useAuth } from "../hooks/useAuth";

const Auth: React.FC = () => {
  const { mode, setMode, loading, errorMsg, login, signup } = useAuth();

  return (
    <HStack
      spacing={8}
      alignItems={{ base: "start", md: "center" }}
      justifyContent={"space-between"}
      minH={"100vh"}
      w={"full"}
      p={{ base: 4, md: 8 }}
      bgColor={"gray.100"}
      bgImage={images.pattern_primary}
      bgSize={"cover"}
    >
      <VStack spacing={12} h={"full"} w={"full"}>
        {/* AUTH HEADER */}
        <HStack justifyContent={"start"} alignItems={"start"} w={"full"}>
          <Image
            src={images.logo_primary_full}
            w={{
              base: 16,
              md: 28,
            }}
          />
        </HStack>

        <SlideFade
          in={true}
          style={{
            width: "100%",
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          offsetY={"50px"}
          transition={{ enter: { duration: 0.7 } }}
        >
          {/* AUTH BODY */}
          <VStack
            spacing={16}
            alignItems={"flex-start"}
            justifyContent={"center"}
            maxW={"container.sm"}
            bgColor={"white"}
            p={{ base: 6, md: 12 }}
            borderRadius={"xl"}
          >
            <VStack
              spacing={4}
              alignItems={"flex-start"}
              justifyContent={"center"}
            >
              <Heading as="h1" fontWeight={"extrabold"} size="2xl">
                {mode === "login" ? "Iniciar sesión" : "Registrarse"}
              </Heading>

              <Text>
                {mode === "login"
                  ? "¡Bienvenido! Por favor, inicia sesión con tu correo electrónico y contraseña."
                  : "¡Bienvenido! Por favor, regístrate con tu correo electrónico y contraseña."}
              </Text>
            </VStack>

            {mode === "login" ? (
              <Login auth={login} loading={loading} />
            ) : (
              <Signup auth={signup} loading={loading} />
            )}

            <Collapse in={!!errorMsg}>
              <HStack alignItems={"flex-start"}>
                <Icon
                  as={HiXMark}
                  color={"red.500"}
                  strokeWidth={1}
                  h={5}
                  w={5}
                  mt={"0.5"}
                />
                <Text color={"red.500"}>{errorMsg}</Text>
              </HStack>
            </Collapse>

            <Text
              fontWeight={"medium"}
              color={"gray.500"}
              fontSize={16}
              alignSelf={"center"}
            >
              {mode === "login"
                ? "¿Todavía no estas registrado? "
                : "¿Ya tienes una cuenta? "}
              <Button
                variant={"link"}
                color={"gray.600"}
                onClick={() => setMode(mode === "login" ? "signup" : "login")}
              >
                {mode === "login" ? "¡Regístrate ahora!" : "¡Inicia sesión!"}
              </Button>
            </Text>
          </VStack>
        </SlideFade>
      </VStack>
    </HStack>
  );
};

export default Auth;

const Login: React.FC<{
  auth: ({ email, password }: { email: string; password: string }) => void;
  loading: boolean;
}> = ({ auth, loading }) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const [valid, setValid] = useState<boolean>(false);

  const [errorMsg, setErrorMsg] = useState<string | undefined>();

  useEffect(() => {
    validateData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password]);

  const validateData = () => {
    setErrorMsg(undefined);

    if (email === "") {
      setValid(false);
    } else if (password === "") {
      setValid(false);
    } else if (!email || !password) {
      setValid(false);
      setErrorMsg("Introce tu correo electrónico y contraseña");
    } else if (
      !email.match(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)
    ) {
      setErrorMsg("Introduce un correo electrónico válido");
      setValid(false);
    } else {
      setValid(true);
    }
  };

  return (
    <>
      <VStack
        spacing={8}
        as={"form"}
        w={"full"}
        alignItems={"flex-start"}
        onSubmit={(e: React.FormEvent<HTMLDivElement>) => {
          e.preventDefault();
          auth({ email, password });
        }}
      >
        <VStack w={"full"} alignItems={"flex-start"} spacing={3}>
          <FormControl id="email" isRequired>
            <FormLabel fontWeight={"medium"}>Correo electrónico</FormLabel>
            <Input
              type="email"
              size={"lg"}
              value={email}
              placeholder={"mail@dominio.com"}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </FormControl>

          <FormControl id="password" isRequired>
            <FormLabel fontWeight={"medium"}>Contraseña</FormLabel>
            <Input
              type="password"
              size={"lg"}
              value={password}
              placeholder={"· · · · · · · · · · · · · · "}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
            />

            <FormHelperText>
              <Text fontWeight={"medium"} fontSize={14} color={"gray.500"}>
                ¿Has olvidado la contraseña?{" "}
                <Link to={"/reset-password"}>
                  <Box
                    as={"span"}
                    fontWeight={"medium"}
                    color={"gray.800"}
                    textDecor={"underline"}
                  >
                    ¡Recupérala!
                  </Box>
                </Link>
              </Text>
            </FormHelperText>
          </FormControl>
        </VStack>

        <Button
          w={"full"}
          justifyContent={"space-between"}
          size={"lg"}
          colorScheme="brand"
          type="submit"
          rightIcon={<Icon as={HiChevronRight} />}
          isLoading={loading}
          isDisabled={!valid}
        >
          Iniciar sesión
        </Button>

        <Collapse in={!!errorMsg}>
          <HStack alignItems={"flex-start"}>
            <Icon
              as={HiXMark}
              color={"red.500"}
              strokeWidth={1}
              h={5}
              w={5}
              mt={"0.5"}
            />
            <Text color={"red.500"}>
              {ERROR_MESSAGES[errorMsg as string] ??
                ERROR_MESSAGES["ERR_GENERIC"]}
            </Text>
          </HStack>
        </Collapse>
      </VStack>
    </>
  );
};

const Signup: React.FC<{
  auth: ({
    email,
    password,
    firstName,
    lastName,
    phoneNumber,
  }: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
  }) => void;
  loading: boolean;
}> = ({ auth, loading }) => {
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const [valid, setValid] = useState<boolean>(false);

  const [errorMsg, setErrorMsg] = useState<string | undefined>();

  useEffect(() => {
    validateData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password]);

  const validateData = () => {
    setErrorMsg(undefined);

    if (email === "") {
      setValid(false);
    } else if (password === "") {
      setValid(false);
    } else if (!email || !password) {
      setValid(false);
      setErrorMsg("Introce tu correo electrónico y contraseña");
    } else if (
      !email.match(/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/)
    ) {
      setErrorMsg("Introduce un correo electrónico válido");
      setValid(false);
    } else if (password.length < 8) {
      setValid(false);
      setErrorMsg("La contraseña debe tener al menos 8 caracteres");
    } else if (firstName === "") {
      setValid(false);
    } else if (lastName === "") {
      setValid(false);
    } else if (phoneNumber === "") {
      setValid(false);
    } else {
      setValid(true);
    }
  };

  return (
    <>
      <VStack
        spacing={8}
        as={"form"}
        w={"full"}
        alignItems={"flex-start"}
        onSubmit={(e: React.FormEvent<HTMLDivElement>) => {
          e.preventDefault();
          auth({
            email,
            password,
            firstName,
            lastName,
            phoneNumber,
          });
        }}
      >
        <VStack w={"full"} alignItems={"flex-start"} spacing={3}>
          <Stack
            direction={{
              base: "column",
              md: "row",
            }}
            justifyContent={"start"}
            alignItems={"start"}
            w={"full"}
          >
            <FormControl id="firstName" isRequired>
              <FormLabel fontWeight={"medium"}>Nombre</FormLabel>
              <Input
                type="text"
                size={"lg"}
                value={firstName}
                placeholder={"Juan"}
                onChange={(e) => {
                  setFirstName(e.target.value);
                }}
              />
            </FormControl>

            <FormControl id="lastName" isRequired>
              <FormLabel fontWeight={"medium"}>Apellido</FormLabel>
              <Input
                type="text"
                size={"lg"}
                value={lastName}
                placeholder={"Gómez"}
                onChange={(e) => {
                  setLastName(e.target.value);
                }}
              />
            </FormControl>
          </Stack>

          <FormControl id="phoneNumber" isRequired>
            <FormLabel fontWeight={"medium"}>Móvil</FormLabel>
            <Input
              type="tel"
              size={"lg"}
              value={phoneNumber}
              placeholder={"+34 623 456 789"}
              onChange={(e) => {
                setPhoneNumber(e.target.value);
              }}
            />
          </FormControl>

          <FormControl id="email" isRequired>
            <FormLabel fontWeight={"medium"}>Correo electrónico</FormLabel>
            <Input
              type="email"
              size={"lg"}
              value={email}
              placeholder={"mail@dominio.com"}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
            />
          </FormControl>

          <FormControl id="password" isRequired>
            <FormLabel fontWeight={"medium"}>Contraseña</FormLabel>
            <Input
              type="password"
              size={"lg"}
              value={password}
              placeholder={"· · · · · · · · · · · · · · "}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
            />
          </FormControl>
        </VStack>

        <Button
          w={"full"}
          justifyContent={"space-between"}
          size={"lg"}
          colorScheme="brand"
          type="submit"
          rightIcon={<Icon as={HiChevronRight} />}
          isLoading={loading}
          isDisabled={!valid}
        >
          Registrarse
        </Button>

        <Collapse in={!!errorMsg}>
          <HStack alignItems={"flex-start"}>
            <Icon
              as={HiXMark}
              color={"red.500"}
              strokeWidth={1}
              h={5}
              w={5}
              mt={"0.5"}
            />
            <Text color={"red.500"}>{errorMsg}</Text>
          </HStack>
        </Collapse>
      </VStack>
    </>
  );
};
